import { useEffect, useMemo, useRef, useState } from "react";
import { api, type Project } from "./api";
import { createVoiceController, type VoiceState, type TranscriptLine } from "./voice";

type Mode = "chat" | "voice";

interface Message {
  role: "user" | "model" | "error";
  text: string;
}

export function App() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("chat");

  // Chat state
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [adrTitle, setAdrTitle] = useState("");
  const [adrBusy, setAdrBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);

  // Voice state
  const [voiceState, setVoiceState] = useState<VoiceState>("idle");
  const [voiceMsg, setVoiceMsg] = useState("");
  const [transcript, setTranscript] = useState<TranscriptLine[]>([]);
  const transcriptBottomRef = useRef<HTMLDivElement>(null);

  const handleTranscript = useRef<(line: TranscriptLine) => void>(() => {});
  handleTranscript.current = (line: TranscriptLine) => {
    setTranscript((prev) => {
      const last = prev[prev.length - 1];
      // Append to the current in-progress line for this role, or start a new one
      if (last && last.role === line.role && !last.final) {
        return [...prev.slice(0, -1), { ...last, text: last.text + line.text, final: line.final }];
      }
      return [...prev, line];
    });
  };

  const voice = useMemo(
    () => createVoiceController(
      (s, m) => {
        setVoiceState(s);
        setVoiceMsg(m ?? "");
        if (s === "idle") setTranscript([]);
      },
      (line) => handleTranscript.current(line),
    ),
    [],
  );

  useEffect(() => {
    api.projects()
      .then((r) => {
        setProjects(r.projects);
        if (r.projects[0]) setActive(r.projects[0].name);
      })
      .catch((e) => setStatusMsg(`projects: ${e.message}`));
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    transcriptBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcript]);

  async function sendChat() {
    const text = input.trim();
    if (!text || chatBusy) return;
    const next: Message[] = [...messages, { role: "user", text }];
    setMessages(next);
    setInput("");
    setChatBusy(true);
    setStatusMsg("");
    try {
      const apiMessages = next.filter((m) => m.role !== "error") as { role: "user" | "model"; text: string }[];
      const res = await api.chat(apiMessages, active ?? undefined);
      setMessages([...next, { role: "model", text: res.text }]);
      // If the reply looks like an ADR draft, pre-fill the title field
      const titleMatch = res.text.match(/^#\s+(.+)/m);
      if (titleMatch?.[1] && !adrTitle) setAdrTitle(titleMatch[1].replace(/^ADR\s*[—-]\s*/i, "").trim());
    } catch (e) {
      setMessages([...next, { role: "error", text: (e as Error).message }]);
    } finally {
      setChatBusy(false);
    }
  }

  async function commitAdr() {
    if (!active || !adrTitle.trim()) {
      setStatusMsg("select a project and enter an ADR title first");
      return;
    }
    const lastModel = [...messages].reverse().find((m) => m.role === "model");
    const body = lastModel?.text ?? "";
    setAdrBusy(true);
    setStatusMsg("");
    try {
      const r = await api.manage({
        action: "commit_adr",
        input: { repo: active, title: adrTitle.trim(), body },
      });
      setStatusMsg(r.ok ? `ADR committed to ${active}` : `error: ${r.error}`);
      if (r.ok) setAdrTitle("");
    } catch (e) {
      setStatusMsg((e as Error).message);
    } finally {
      setAdrBusy(false);
    }
  }

  function handleKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); }
  }

  const lastModelText = [...messages].reverse().find((m) => m.role === "model")?.text ?? "";
  const showAdrStrip = mode === "chat" && lastModelText.length > 0 && messages[messages.length - 1]?.role !== "error";

  return (
    <div className="app">
      {/* Topbar */}
      <header className="topbar">
        <span className="brand">DOPE</span>
        <span className="tagline">tactical institutional memory</span>
        <span className="spacer" />
        {active && <span className="pill">{active}</span>}
        {statusMsg && <span style={{ fontSize: 11, color: "var(--text-dim)", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{statusMsg}</span>}
      </header>

      {/* Sidebar — repo list */}
      <aside className="sidebar">
        <div className="sidebar-head">
          <h3>Projects</h3>
          <button
            className="btn"
            style={{ fontSize: 11, padding: "4px 9px" }}
            onClick={async () => {
              const name = prompt("New repo name?");
              if (!name) return;
              try {
                const r = await api.manage({ action: "create_repo", input: { name, private: true } });
                if (r.ok) {
                  const list = await api.projects();
                  setProjects(list.projects);
                  setActive(name);
                } else {
                  setStatusMsg(`error: ${r.error}`);
                }
              } catch (e) { setStatusMsg((e as Error).message); }
            }}
          >
            + New
          </button>
        </div>
        <div className="sidebar-list">
          {projects.length === 0 && <div className="hint">No projects yet.</div>}
          {projects.map((p) => (
            <button
              key={p.full_name}
              className={`project-btn ${active === p.name ? "active" : ""}`}
              onClick={() => setActive(p.name)}
            >
              <div className="p-name">{p.name}</div>
              <div className="p-meta">{p.private ? "private" : "public"}</div>
            </button>
          ))}
        </div>
      </aside>

      {/* Main */}
      <section className="main">
        {/* Mode tabs */}
        <div className="mode-tabs">
          <button className={`mode-tab ${mode === "chat" ? "active" : ""}`} onClick={() => setMode("chat")}>
            Chat
          </button>
          <button className={`mode-tab ${mode === "voice" ? "active" : ""}`} onClick={() => setMode("voice")}>
            Live Voice
          </button>
        </div>

        {/* Chat */}
        {mode === "chat" && (
          <div className="chat-wrap">
            <div className="chat-messages">
              {messages.length === 0 && (
                <div className="empty">
                  Ask DOPE anything — decisions, context, ADR drafts.
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`msg ${m.role}`}>
                  <div className="msg-bubble">{m.text}</div>
                  <div className="msg-meta">{m.role === "user" ? "you" : m.role === "error" ? "error" : "dope"}</div>
                </div>
              ))}
              {chatBusy && (
                <div className="msg model">
                  <div className="msg-bubble" style={{ color: "var(--text-dim)" }}>thinking…</div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {showAdrStrip && (
              <div className="adr-strip">
                <span className="label">Commit as ADR →</span>
                <input
                  placeholder="ADR title"
                  value={adrTitle}
                  onChange={(e) => setAdrTitle(e.target.value)}
                />
                <button className="btn primary" onClick={commitAdr} disabled={adrBusy || !active}>
                  {adrBusy ? "Committing…" : "Commit"}
                </button>
              </div>
            )}

            <div className="chat-input-row">
              <textarea
                className="chat-input"
                placeholder={`Message DOPE${active ? ` — working in ${active}` : ""}…`}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                rows={1}
              />
              <button className="btn primary" onClick={sendChat} disabled={chatBusy || !input.trim()}>
                Send
              </button>
            </div>
          </div>
        )}

        {/* Voice */}
        {mode === "voice" && (
          <div className="voice-wrap">
            {transcript.length > 0 ? (
              <div className="voice-transcript">
                {transcript.map((line, i) => (
                  <div key={i} className={`vt-line ${line.role} ${line.final ? "final" : "partial"}`}>
                    <span className="vt-who">{line.role === "user" ? "you" : "dope"}</span>
                    <span className="vt-text">{line.text}</span>
                  </div>
                ))}
                <div ref={transcriptBottomRef} />
              </div>
            ) : (
              <div className="voice-status">
                {voiceState === "idle" && "Press to start a live voice session with DOPE."}
                {voiceState === "connecting" && "Connecting…"}
                {voiceState === "live" && "Live — speak now."}
                {voiceState === "error" && `Error: ${voiceMsg}`}
              </div>
            )}
            <div className="voice-controls">
              <button
                className={`voice-btn ${voiceState}`}
                onClick={() => voiceState === "live" ? voice.stop() : voice.start()}
                aria-label={voiceState === "live" ? "End voice session" : "Start voice session"}
              >
                {voiceState === "live" ? "⏹" : "🎙"}
              </button>
              <span className="voice-label">
                {voiceState === "live" ? "tap to end" : "tap to speak"}
              </span>
            </div>
            {voiceState === "error" && transcript.length > 0 && (
              <div className="voice-status" style={{ color: "var(--error, #f87171)" }}>Error: {voiceMsg}</div>
            )}
            {active && <span className="pill" style={{ marginTop: 8 }}>context: {active}</span>}
          </div>
        )}
      </section>
    </div>
  );
}
